import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getSalaries, addSalary, deleteSalary } from '../api';
import {
  formatNumber,
  parseFormattedNumber,
  convertDateForApi,
  formatDateForDisplay,
  getTodayByLocale,
  getMonthStartByLocale,
  getDateConfig,
  switchDateLocale,
  usePagination,
} from '../utils';
import DatePicker from 'react-multi-date-picker';

function Salaries({ dateLocale }) {
  const dateConfig = getDateConfig(dateLocale);

  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    amount: '',
    salary_date: getTodayByLocale(dateLocale),
    description: ''
  });
  const [filters, setFilters] = useState({
    startDate: getMonthStartByLocale(dateLocale),
    endDate: getTodayByLocale(dateLocale),
  });
  const prevLocaleRef = useRef(dateLocale);
  const salariesPagination = usePagination(salaries);
  const goToSalariesPage = salariesPagination.goToPage;

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };
  useEffect(() => {
    const prevLocale = prevLocaleRef.current;
    if (prevLocale !== dateLocale) {
      setFormData((prev) => ({
        ...prev,
        salary_date: switchDateLocale(prev.salary_date, prevLocale, dateLocale),
      }));
      setFilters((prev) => ({
        ...prev,
        startDate:
          prev.startDate
            ? switchDateLocale(prev.startDate, prevLocale, dateLocale)
            : getMonthStartByLocale(dateLocale),
        endDate:
          prev.endDate
            ? switchDateLocale(prev.endDate, prevLocale, dateLocale)
            : getTodayByLocale(dateLocale),
      }));
      prevLocaleRef.current = dateLocale;
    }
  }, [dateLocale]);

  const loadSalaries = useCallback(
    async (currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {};
      const startDateValue = convertDateForApi(currentFilters.startDate, dateLocale);
      if (startDateValue) {
        params.start_date = startDateValue;
      }
      const endDateValue = convertDateForApi(currentFilters.endDate, dateLocale);
      if (endDateValue) {
        params.end_date = endDateValue;
      }
      const response = await getSalaries(params);
      setSalaries(response.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری حقوق‌ها');
    } finally {
      setLoading(false);
    }
    },
    [filters, dateLocale]
  );

  useEffect(() => {
    loadSalaries(filters);
  }, [filters, loadSalaries]);

  useEffect(() => {
    goToSalariesPage(1);
  }, [salaries, goToSalariesPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.salary_date) {
      setError('مبلغ و تاریخ الزامی است');
      return;
    }

    try {
      const gregorianDate = convertDateForApi(formData.salary_date, dateLocale);
      await addSalary({
        amount: parseFloat(parseFormattedNumber(formData.amount)),
        salary_date: gregorianDate,
        description: formData.description
      });
      setSuccess('حقوق با موفقیت ثبت شد');
      setFormData({
        amount: '',
        salary_date: getTodayByLocale(dateLocale),
        description: ''
      });
      loadSalaries(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در ثبت حقوق');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این حقوق مطمئن هستید؟')) return;
    
    try {
      await deleteSalary(id);
      setSuccess('حقوق حذف شد');
      loadSalaries(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در حذف حقوق');
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: getMonthStartByLocale(dateLocale),
      endDate: getTodayByLocale(dateLocale),
    });
  };
  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      <div className="card border-0 shadow-sm">
        <div className="card-body d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <h2 className="h5 mb-1">پرتال مدیریت حقوق</h2>
            <p className="text-muted small mb-0">ثبت و مدیریت پرداخت‌های پرسنل</p>
          </div>
          <span className="badge bg-primary text-white px-3 py-2">{salaries.length} پرداخت فعال</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-0">ثبت حقوق پرسنل</h2>
              <p className="text-muted small mb-0">اطلاعات پرداخت جدید را وارد کنید</p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">مبلغ (تومان)</label>
                  <div className="input-group">
                    <span className="input-group-text">تومان</span>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.amount}
                      onChange={(e) => {
                        const rawValue = parseFormattedNumber(e.target.value);
                        if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                          setFormData({ ...formData, amount: formatNumber(rawValue) });
                        }
                      }}
                      placeholder="مبلغ حقوق را وارد کنید"
                    />
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">تاریخ پرداخت</label>
                  <DatePicker
                    value={formData.salary_date}
                    onChange={(date) => {
                      if (date) {
                        setFormData({ ...formData, salary_date: date });
                      }
                    }}
                    calendar={dateConfig.calendar}
                    locale={dateConfig.locale}
                    calendarPosition="bottom-right"
                    inputClass="form-control"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">توضیحات</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="توضیحات (اختیاری)"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary w-100">
                    ثبت حقوق
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="d-grid gap-3">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-bottom-0 pb-0">
                <h2 className="h6 mb-1">فیلتر لیست حقوق‌ها</h2>
                <p className="text-muted small mb-0">بازه مورد نظر را انتخاب کنید</p>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-5">
                    <label className="form-label">از تاریخ</label>
                    <DatePicker
                      value={filters.startDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-5">
                    <label className="form-label">تا تاریخ</label>
                    <DatePicker
                      value={filters.endDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                      بازنشانی
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h5 mb-1">لیست حقوق‌ها</h2>
                  <p className="text-muted small mb-0">پیگیری پرداخت‌های ثبت شده</p>
                </div>
                <span className="badge bg-secondary text-white px-3 py-2">
                  {salaries.length} مورد ثبت شده
                </span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">ردیف</th>
                        <th scope="col">مبلغ</th>
                        <th scope="col">تاریخ</th>
                        <th scope="col">توضیحات</th>
                        <th scope="col" className="text-end">عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salariesPagination.totalItems === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            هنوز حقوقی ثبت نشده است
                          </td>
                        </tr>
                      )}
                      {salariesPagination.paginatedItems.map((salary, index) => (
                        <tr key={salary.id}>
                          <td>
                            <span className="badge bg-primary text-white">{salariesPagination.rangeStart + index}</span>
                          </td>
                          <td>
                            <div className="fw-bold text-dark">
                              {formatNumber(salary.amount)}
                            </div>
                            <small className="text-muted">تومان</small>
                          </td>
                          <td>{formatDateForDisplay(salary.salary_date, dateLocale)}</td>
                          <td>
                            {salary.description ? (
                              <span>{salary.description}</span>
                            ) : (
                              <span className="text-muted">بدون توضیح</span>
                            )}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(salary.id)}
                            >
                              حذف
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderPaginationControls(salariesPagination)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Salaries;

